/**
 * Display helpers for GET /vuser/dialogs/{id}:
 * - reasoning may appear as separate message rows (e.g. role "reasoning")
 * - assistant pipeline steps may use reasoning_messages[] instead of one JSON blob
 */

const REASONING_ROLES = new Set([
  'reasoning',
  'assistant_reasoning',
  'thinking',
  'chain_of_thought',
  'cot',
]);

export function isDialogReasoningRole(role: string | undefined): boolean {
  if (!role) return false;
  return REASONING_ROLES.has(role.toLowerCase());
}

function reasoningPartLabel(item: Record<string, unknown>, index: number): string {
  const raw =
    item.agent_name ?? item.name ?? item.agent ?? item.role ?? `Agent ${index + 1}`;
  return String(raw);
}

function reasoningPartBody(item: Record<string, unknown>): string {
  const v =
    (typeof item.content === 'string' && item.content) ||
    (typeof item.text === 'string' && item.text) ||
    (typeof item.message === 'string' && item.message) ||
    '';
  if (v) return v;
  try {
    return JSON.stringify(item, null, 2);
  } catch {
    return String(item);
  }
}

function formatReasoningPartsList(arr: unknown[]): string {
  return arr
    .map((item, i) => {
      if (item == null) return '';
      if (typeof item === 'string') return item.trim();
      if (typeof item !== 'object') return String(item);
      const o = item as Record<string, unknown>;
      const label = reasoningPartLabel(o, i);
      const body = reasoningPartBody(o);
      return `${label}\n${body}`.trim();
    })
    .filter((s) => s.trim().length > 0)
    .join('\n\n—\n\n');
}

/** If `arr` looks like a list of reasoning fragments (objects with text fields), format them; else null. */
function tryFormatReasoningArray(arr: unknown): string | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = arr[0];
  if (first == null) return null;
  if (typeof first === 'string') return formatReasoningPartsList(arr);
  if (typeof first === 'object') {
    const o = first as Record<string, unknown>;
    if ('content' in o || 'text' in o || 'message' in o || 'agent_name' in o || 'name' in o) {
      return formatReasoningPartsList(arr);
    }
  }
  return null;
}

/**
 * Label for one reasoning step in GET /vuser/dialogs/{id} — always prefer agent name (`agent_name`), never "Step N".
 */
export function getPipelineStepDisplayName(step: unknown, index: number): string {
  const s = step && typeof step === 'object' ? (step as Record<string, unknown>) : {};
  const direct =
    (typeof s.agent_name === 'string' && s.agent_name.trim()) ||
    (typeof s.name === 'string' && s.name.trim());
  if (direct) return direct;
  const parts = (s.reasoning_messages ?? s.reasoningMessages) as unknown;
  if (Array.isArray(parts)) {
    for (const p of parts) {
      if (p && typeof p === 'object') {
        const o = p as Record<string, unknown>;
        const n =
          (typeof o.agent_name === 'string' && o.agent_name.trim()) ||
          (typeof o.name === 'string' && o.name.trim());
        if (n) return n;
      }
    }
  }
  const typeRaw = typeof s.type === 'string' ? s.type.trim() : '';
  if (typeRaw && !/^ai$/i.test(typeRaw)) return typeRaw;
  return `Unknown agent (${index + 1})`;
}

/**
 * Human-readable body for one assistant pipeline step (handles reasoning_messages / legacy single fields).
 */
export function getDialogStepBodyDisplay(step: unknown): string {
  const s = step as Record<string, unknown>;

  /** New VUS shape: single `content` string, no reasoning fragments — show as-is. */
  if (
    typeof s.content === 'string' &&
    s.content.trim() &&
    !tryFormatReasoningArray(s.reasoning_messages) &&
    !tryFormatReasoningArray(s.reasoningMessages)
  ) {
    return s.content.trim();
  }

  const fromReasoning =
    tryFormatReasoningArray(s.reasoning_messages) ??
    tryFormatReasoningArray(s.reasoningMessages) ??
    tryFormatReasoningArray(s.intermediate_messages) ??
    tryFormatReasoningArray(s.intermediateMessages);
  if (fromReasoning) return fromReasoning;

  const maybeMessages = tryFormatReasoningArray(s.messages);
  if (maybeMessages) return maybeMessages;

  const keys = ['content', 'output', 'text', 'message', 'body'] as const;
  for (const k of keys) {
    const v = s[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  try {
    return JSON.stringify(s, null, 2);
  } catch {
    return String(s.id ?? step);
  }
}
